import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { WizardSteps, type WizardStep } from "../../../design/components";
import { useEntity } from "../../../api/queries";
import type { ConnectionMethod } from "../../../api/types";
import { enabledFamiliesOf } from "../mapping/profileState";
import { useEntityProfiles } from "../mapping/useProfileVersion";
import { useConnection } from "./onboardApi";
import { StepConnection } from "./steps/StepConnection";
import { StepGoLive } from "./steps/StepGoLive";
import { StepIdentity } from "./steps/StepIdentity";
import { StepMapping } from "./steps/StepMapping";
import { StepTest } from "./steps/StepTest";
import { StepTestDocument } from "./steps/StepTestDocument";
import { codeFor, testDocumentKey, useOnboardDraft } from "./useOnboardDraft";

export const STEPS = ["identity", "connection", "test", "mapping", "testdoc", "golive"] as const;
export type StepKey = (typeof STEPS)[number];

const LABEL_KEYS: Record<StepKey, string> = {
  identity: "onboard.wizard.steps.identity",
  connection: "onboard.wizard.steps.connection",
  test: "onboard.wizard.steps.test",
  mapping: "onboard.wizard.steps.mapping",
  testdoc: "onboard.wizard.steps.testdoc",
  golive: "onboard.wizard.steps.golive",
};

const LABEL_DEFAULTS: Record<StepKey, string> = {
  identity: "Company identity",
  connection: "Connection",
  test: "Test",
  mapping: "Mapping",
  testdoc: "Test document",
  golive: "Go live",
};

/** Settings the operator saved; discovery output and the last probe are not connection settings. */
const INTERNAL_KEYS = new Set(["erp_fields", "sample_document", "discovered_at", "last_probe"]);

export function OnboardWizard() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const entityId = params.get("entityId") ?? undefined;
  const entity = useEntity(entityId);
  const connection = useConnection(entityId);
  const profiles = useEntityProfiles(entityId);
  const draftName = useOnboardDraft((draft) => draft.identity.legalName);
  const testDocument = useOnboardDraft((draft) => draft.testDocument);
  const setTestDocument = useOnboardDraft((draft) => draft.setTestDocument);

  const configured =
    Object.keys(connection.data?.config ?? {}).filter((key) => !INTERNAL_KEYS.has(key)).length > 0;
  const tested = connection.data?.lastProbe?.ok === true;
  // Every family the company chose needs its own published mapping and its own green dry run.
  const families = enabledFamiliesOf(entity.data?.enabledDocTypes);
  const published =
    profiles.data !== undefined &&
    families.every((family) => profiles.data.find((item) => item.family === family)?.activeVersion != null);
  // The server keeps no record of a dry run, so this one step's progress is session state.
  const documentRun = entityId
    ? families.every((family) => testDocument[testDocumentKey(entityId, family)] === true)
    : false;

  const firstIncomplete: StepKey = !entityId
    ? "identity"
    : !configured
      ? "connection"
      : !tested
        ? "test"
        : !published
          ? "mapping"
          : !documentRun
            ? "testdoc"
            : "golive";

  const asked = params.get("step") as StepKey | null;
  const limit = STEPS.indexOf(firstIncomplete);
  const askedIndex = asked ? STEPS.indexOf(asked) : -1;
  const current: StepKey = askedIndex >= 0 && askedIndex <= limit ? STEPS[askedIndex]! : firstIncomplete;

  function go(step: StepKey, id = entityId): void {
    const next = new URLSearchParams(params);
    next.set("step", step);
    if (id) next.set("entityId", id);
    // Stamping the new id replaces the entry: going back to a wizard URL with no
    // entityId would offer step 1 again and create the company a second time.
    setParams(next, { replace: id !== entityId });
  }

  const steps: WizardStep[] = STEPS.map((key, index) => ({
    key,
    label: t(LABEL_KEYS[key], LABEL_DEFAULTS[key]),
    disabled: index > limit,
  }));

  const method = (entity.data?.connectionMethod ?? connection.data?.method ?? "direct_api") as ConnectionMethod;
  const code = entity.data?.code ?? codeFor(draftName);
  // Which step comes next is server state; painting one before it arrives makes the
  // wizard jump under the operator on a reload.
  const loading =
    Boolean(entityId) && (connection.isPending || entity.isPending || profiles.isPending);

  return (
    <div className="page">
      <div className="page-head">
        <div className="grow">
          <div className="page-title">
            <h1>{t("onboard.wizard.title", "Onboard a Company")}</h1>
          </div>
          <p className="page-sub">
            {t(
              "onboard.wizard.subtitle",
              "Six steps from a company's identity to its first live document. Progress is saved as you go, so you can leave and come back.",
            )}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => navigate("/companies")}>
            {t("onboard.wizard.saveAndExit", "Save and exit")}
          </button>
        </div>
      </div>

      <WizardSteps
        steps={steps}
        current={current}
        label={t("onboard.wizard.stepsLabel", "Onboarding steps")}
        onSelect={(key) => go(key as StepKey)}
      />

      {loading ? (
        <p className="page-sub">{t("onboard.wizard.loading", "Loading this company's progress…")}</p>
      ) : null}

      {!loading && current === "identity" ? (
        <StepIdentity onCreated={(id) => go("connection", id)} />
      ) : null}
      {!loading && current === "connection" && entityId ? (
        <StepConnection entityId={entityId} code={code} onSaved={() => go("test")} />
      ) : null}
      {!loading && current === "test" && entityId ? (
        <StepTest
          entityId={entityId}
          method={method}
          families={families}
          onContinue={() => go("mapping")}
          onBack={() => go("connection")}
        />
      ) : null}
      {!loading && current === "mapping" && entityId ? (
        <StepMapping entityId={entityId} onContinue={() => go("testdoc")} />
      ) : null}
      {!loading && current === "testdoc" && entityId ? (
        <StepTestDocument
          entityId={entityId}
          onContinue={() => go("golive")}
          onPassed={(family, passed) => setTestDocument(entityId, family, passed)}
        />
      ) : null}
      {!loading && current === "golive" && entityId ? <StepGoLive entityId={entityId} /> : null}
    </div>
  );
}
